import { Dispatch, SetStateAction, useContext, useRef } from 'react';
import cn from 'classnames';
import { Form } from 'react-bootstrap';
import {
  MdKeyboardArrowDown,
  MdKeyboardArrowUp,
  MdArrowCircleRight,
  MdEdit,
  MdDelete
} from 'react-icons/md';
import { BiSolidDuplicate } from 'react-icons/bi';
import { useDrag, useDrop } from 'react-dnd';
import * as R from 'ramda';
import { IconButton } from '@Components/IconButton';
import { DAY_OF_WEEK_OPTIONS } from '@src/utils/date-utils';
import { ActionsMenu, ActionsMenuItem } from '../ActionsMenu/ActionsMenu';
import { FormContext, WtoRule, WtoVariant } from '../wto-form-context';
import './RulesFormModal/RulesFormModal.scss';

interface RuleCardProps {
  data: WtoRule;
  index: number;
  show: boolean;
  hideCard: (index: number) => void;
  ruleModal: (index?: number) => void;
  reorderEnabled: boolean;
  setDuplicateRuleIndex: Dispatch<SetStateAction<number>>;
  showRuleCards: boolean[];
  setShowRuleCards: Dispatch<SetStateAction<boolean[]>>;
}

export const RuleCard = ({
  data,
  index,
  show,
  hideCard,
  ruleModal,
  reorderEnabled,
  setDuplicateRuleIndex,
  showRuleCards,
  setShowRuleCards
}: RuleCardProps) => {
  const { wtoFormObj, setWtoFormObj } = useContext(FormContext);

  const cardIndex = Number(data.rule_order);

  const updateFormField = (key, value) =>
    setWtoFormObj((current: any) => ({ ...current, [key]: value }));

  interface RuleCardDragItem {
    id: string;
    index: number;
    data: any;
  }

  const id = `rule:${data.rule_name}`;

  const RULE_CARD_DND_TYPE = 'RULE_CARD_DND_TYPE' as const;

  const ref = useRef<HTMLDivElement>(null);

  const onDrop = (droppedData, droppedIndex) => {
    const reorderedRules = R.clone(wtoFormObj.rules);
    const reorderedCardsShown = [...showRuleCards];
    reorderedRules[droppedIndex] = {
      ...data,
      rule_order: droppedIndex
    };
    reorderedRules[index] = {
      ...droppedData,
      rule_order: index
    };
    reorderedCardsShown[index] = showRuleCards[droppedIndex];
    reorderedCardsShown[droppedIndex] = showRuleCards[index];
    setShowRuleCards(reorderedCardsShown);
    setWtoFormObj((current: any) => ({ ...current, rules: reorderedRules }));
  };

  const [{ isDragging }, drag, preview] = useDrag({
    type: RULE_CARD_DND_TYPE,
    item: { id, index, data },
    canDrag: reorderEnabled,
    collect: monitor => ({
      isDragging: monitor.isDragging()
    })
  });

  const [{ isOver }, drop] = useDrop({
    accept: RULE_CARD_DND_TYPE,
    drop: (item: RuleCardDragItem) => onDrop(item.data, item.index),
    collect: monitor => ({
      isOver: monitor.isOver()
    })
  });

  const deleteRule = () => {
    // Plover.rules is only weakly typed (Record<string, any>[]) in bpm-types; every
    // rule read off wtoFormObj is actually WtoRule-shaped at runtime.
    const updatedRules = (wtoFormObj.rules as WtoRule[]).filter(
      (_, ruleIndex) => ruleIndex !== cardIndex
    );
    const updatedCardsShown = showRuleCards.filter(
      (_, cardShownIndex) => cardShownIndex !== cardIndex
    );
    [...updatedRules].forEach((rule, ruleIndex) => {
      const ruleOrder = Number(rule.rule_order);
      updatedRules[ruleIndex] = {
        ...rule,
        rule_order: ruleOrder > cardIndex ? (ruleOrder - 1).toString() : ruleOrder.toString()
      };
    });
    updateFormField('rules', updatedRules);
    setShowRuleCards(updatedCardsShown);
  };

  // react-dnd's connector functions read the ref via their own internal effect, not at render time
  // eslint-disable-next-line react-hooks/refs
  drop(drag(preview(ref)));

  return (
    <div
      ref={ref}
      className={cn('rule-card', {
        reorderable: reorderEnabled,
        'is-dragging': reorderEnabled && isDragging,
        'is-under': reorderEnabled && isOver && !isDragging
      })}
      id={id}
      data-testid={`rule-card-${index}`}
    >
      {reorderEnabled ? (
        <div className='rule-title'>
          <div className='rule-index'>{Number(data.rule_order) + 1}</div>
          <span className='rule-name'>{data.rule_name}</span>
        </div>
      ) : (
        <>
          <div className='rule-header'>
            <div className='rule-title'>
              <div className='rule-index'>{Number(data.rule_order) + 1}</div>
              <span className='rule-name'>{data.rule_name}</span>
            </div>
            <RuleToolbar
              index={index}
              show={show}
              status={wtoFormObj.status}
              onToggleShow={() => hideCard(index)}
              onEdit={() => ruleModal(cardIndex)}
              onDuplicate={() => setDuplicateRuleIndex(index)}
              onDelete={deleteRule}
            />
          </div>
          {show && <RuleSpecifications data={data} variants={wtoFormObj.variants} />}
        </>
      )}
    </div>
  );
};

interface RuleToolbarProps {
  index: number;
  show: boolean;
  status: string;
  onToggleShow: () => void;
  onEdit: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
}

const RuleToolbar = ({
  index,
  show,
  status,
  onToggleShow,
  onEdit,
  onDuplicate,
  onDelete
}: RuleToolbarProps) => {
  const normalizedStatus = status.toLocaleLowerCase();
  const canEdit = normalizedStatus === 'production' || normalizedStatus === 'draft';
  const canDuplicateOrDelete = normalizedStatus === 'draft';

  const items: ActionsMenuItem[] = [
    { key: 'edit', label: 'Edit', icon: <MdEdit />, onClick: onEdit },
    ...(canDuplicateOrDelete
      ? [
          {
            key: 'duplicate',
            label: 'Duplicate',
            icon: <BiSolidDuplicate />,
            onClick: onDuplicate
          },
          { key: 'delete', label: 'Delete', icon: <MdDelete />, onClick: onDelete }
        ]
      : [])
  ];

  return (
    <div className='rule-toolbar'>
      <IconButton
        onClick={e => {
          e.preventDefault();
          onToggleShow();
        }}
      >
        {show ? <MdKeyboardArrowDown /> : <MdKeyboardArrowUp />}
      </IconButton>
      {canEdit && (
        <ActionsMenu
          items={items}
          popoverId={`rule-actions-${index}`}
          menuTestId={`rule-actions-menu-${index}`}
          toggleTestId={`rule-actions-toggle-${index}`}
        />
      )}
    </div>
  );
};

const isValid = value => !R.isNil(value) && !R.isEmpty(value);

interface RuleSpecificationsProps {
  data: WtoRule;
  variants: WtoVariant[];
}

const RuleSpecifications = ({ data, variants }: RuleSpecificationsProps) => (
  <div className='rule-specifications'>
    <SplitSpecification data={data} variants={variants} />
    {(isValid(data.conditions) || data.timeframe_enabled) && (
      <ConditionsSpecification data={data} />
    )}
    {isValid(data.additional_utm) &&
      data.additional_utm.map((utm: any, utmIndex: number) => (
        <UtmSpecification key={utmIndex} utm={utm} />
      ))}
    {isValid(data.cookie_operation_url) && (
      <CookieUrlSpecification cookieUrls={data.cookie_operation_url} />
    )}
    {isValid(data.cookie_operation) && (
      <CookieOperationSpecification cookieOperations={data.cookie_operation} />
    )}
    {isValid(data.dow_operation) && <DowSpecification dowOperations={data.dow_operation} />}
  </div>
);

interface SplitSpecificationProps {
  data: WtoRule;
  variants: WtoVariant[];
}

const SplitSpecification = ({ data, variants }: SplitSpecificationProps) => {
  const getVariantUrl = (id: string) =>
    variants.find(v => v.variant_id === id)?.variant_url ?? null;

  return (
    <div className='rule-specification'>
      <div className='rule-spec-body'>
        <div className='rule-spec-row'>
          <div className='operator'>SPLIT</div>
          <div className='rule-spec-col'>
            {data.variant_splits.map((variantSplit, splitIndex) => (
              <div className='rule-spec-row' key={splitIndex}>
                <div className='rule-spec-col plover'>
                  {variantSplit.variant_id}
                  <span className='light-text'>{getVariantUrl(variantSplit.variant_id)}</span>
                </div>
                <div className='plover-percentage'>{variantSplit.split}%</div>
              </div>
            ))}
          </div>
        </div>
        {isValid(data.customRetention) && (
          <div className='rule-spec-row retention'>
            <Form.Check inline label='Reset Variant Retention' type='checkbox' checked readOnly />
            <div className='rule-spec-col timestamp'>
              Start Date & Time:
              <span className='light-text'>{data.customRetention.formattedStartDate}</span>
            </div>
            <div className='rule-spec-col timezone'>
              Timezone:
              <span className='light-text'>{data.customRetention.time_zone}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const ConditionsSpecification = ({ data }: { data: WtoRule }) => (
  <div className='rule-specification'>
    <span className='rule-spec-title'>Conditions</span>
    <hr />
    <div className='rule-spec-body'>
      {isValid(data.conditions) &&
        data.conditions.map((condition: any, conditionIndex) => (
          <div className='rule-spec-row' key={conditionIndex}>
            <div className='operator'>{condition.operator}</div>
            <div className='trigger'>{condition.trigger}</div>
            <div className='operator rule'>{condition.rule}</div>
            <div className='value'>{condition.value}</div>
          </div>
        ))}
      {data.timeframe_enabled && data.timeframe && (
        <div className='rule-spec-row'>
          <div className='operator'>AND</div>
          <div className='time-frame-card'>
            <div>
              <div>
                <span className='time-frame-header'>Start: </span>
                <span className='time-frame-value'>{data.timeframe.formattedStartDateTime}</span>
              </div>
              <div>
                <span className='time-frame-header'>End: </span>
                <span className='time-frame-value'>{data.timeframe.formattedEndDateTime}</span>
              </div>
            </div>
            <span className='time-frame-header'>Timezone: </span>
            <span className='time-frame-value'>{data.timeframe.time_zone}</span>
          </div>
        </div>
      )}
    </div>
  </div>
);

const UtmOperations = ({ operations }: { operations: any[] }) => (
  <div className='rule-spec-row'>
    <div className='operator'>ALSO</div>
    <div className='rule-spec-col'>
      {operations?.map((utmData: any, idx: number) => (
        <div key={idx} className='rule-spec-row'>
          <div className='operation-name'>{utmData.operation}:</div>
          {utmData.operation !== 'Replace' ? (
            <div className='light-text'>
              {utmData.utm_key}
              {utmData.utm_value && '='}
              {utmData.utm_value}
            </div>
          ) : (
            <>
              <div className='light-text'>
                {utmData.utm_key}
                {utmData.utm_value && '='}
                {utmData.utm_value}
              </div>
              <div className='icon-wrapper'>
                <MdArrowCircleRight />
              </div>
              <div className='light-text'>
                {utmData.replace_key}
                {utmData.replace_value && '='}
                {utmData.replace_value}
              </div>
            </>
          )}
        </div>
      ))}
    </div>
  </div>
);

const UtmSpecification = ({ utm }: { utm: any }) => (
  <div>
    {isValid(utm.additional_utm_operation) && isValid(utm.additional_utm_url) && (
      <div className='rule-specification'>
        <span className='rule-spec-title'>URL for UTM Parameters</span>
        <hr />
        <div className='rule-spec-body'>
          {utm.additional_utm_url.map((variant, variantIndex) => (
            <div key={variantIndex} className='rule-spec-row utm-variants'>
              <div className='utm-variant-id'>{variant.utm_variant_id}</div>
              <div className='light-text'>{variant.utm_variant_url}</div>
            </div>
          ))}
        </div>
      </div>
    )}
    {isValid(utm.additional_utm_operation) && (
      <div className='rule-specification'>
        {isValid(utm.additional_utm_url) && (
          <>
            <span className='rule-spec-title'>URL Parameter Operations</span>
            <hr />
          </>
        )}
        <div className='rule-spec-body'>
          <UtmOperations operations={utm.additional_utm_operation} />
        </div>
      </div>
    )}
  </div>
);

const CookieUrlSpecification = ({ cookieUrls }: { cookieUrls: any[] }) => (
  <div className='rule-specification'>
    <span className='rule-spec-title'>URL for Cookie Updates</span>
    <hr />
    <div className='rule-spec-body'>
      {cookieUrls.map((cookie, cookieIndex) => (
        <div className='rule-spec-row' key={cookieIndex}>
          <div className='rule-spec-col'>{cookie.cookie_variant_id}</div>
          <div className='rule-spec-col light-text'>{cookie.cookie_variant_url}</div>
        </div>
      ))}
    </div>
  </div>
);

const CookieOperationSpecification = ({ cookieOperations }: { cookieOperations: any[] }) => (
  <div className='rule-specification'>
    <span className='rule-spec-title'>Cookie Operations</span>
    <hr />
    <div className='rule-spec-body'>
      <div className='rule-spec-row'>
        <div className='operator'>ALSO</div>
        <div className='rule-spec-col'>
          {cookieOperations?.map((cookieData: any, idx: number) => (
            <div key={idx} className='rule-spec-row'>
              <div className='operation-name'>{cookieData.operation}:</div>
              {cookieData.operation !== 'Replace' ? (
                <div className='light-text'>
                  {cookieData.cookie_key}
                  {cookieData.cookie_value && '='}
                  {cookieData.cookie_value}
                </div>
              ) : (
                <>
                  <div className='light-text'>
                    {cookieData.cookie_key}
                    {cookieData.cookie_value && '='}
                    {cookieData.cookie_value}
                  </div>
                  <div className='icon-wrapper'>
                    <MdArrowCircleRight />
                  </div>
                  <div className='light-text'>
                    {cookieData.cki_replace_key}
                    {cookieData.cki_replace_value && '='}
                    {cookieData.cki_replace_value}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

const DowVariants = ({ variants }: { variants: any[] }) => (
  <div className='rule-spec-col dow-name'>
    {variants.map((variant, variantIndex) => (
      <div className='rule-spec-row dow-variant' key={variantIndex}>
        <div className='rule-spec-col dow-name'>
          <div className='rule-spec-row'>{variant.name}</div>
          <div className='rule-spec-row light-text'>{variant.url}</div>
        </div>
        <div className='rule-spec-col dow-percentage'>{variant.split}%</div>
      </div>
    ))}
  </div>
);

const DowSpecification = ({ dowOperations }: { dowOperations: any[] }) => (
  <div className='rule-specification'>
    <span className='rule-spec-title'>Day of the Week</span>
    <hr />
    <div className='rule-spec-body'>
      {dowOperations.map((operation, operationIndex) => (
        <div className='rule-spec-row' key={operationIndex}>
          <div className='days'>
            {DAY_OF_WEEK_OPTIONS.map((day, dayIndex) => (
              <span key={dayIndex} className={operation.day.includes(day.number) ? 'selected' : ''}>
                {day.label}
              </span>
            ))}
          </div>
          <div className='rule-spec-row dow-details'>
            {isValid(operation.formattedEndTime) && isValid(operation.formattedStartTime) && (
              <div className='rule-spec-col time-range'>
                <div>
                  <span>Start:</span>
                  <span className='time-of-day light-text'>
                    {operation.formattedStartTime.split(' ')[1]}
                  </span>
                </div>
                <div>
                  <span>End:</span>
                  <span className='time-of-day light-text'>
                    {operation.formattedEndTime.split(' ')[1]}
                  </span>
                </div>
              </div>
            )}
            <DowVariants variants={operation.variants} />
          </div>
        </div>
      ))}
    </div>
  </div>
);
